"use client";

import { useEffect, useState, useEffectEvent } from "react";
import { getPublisherFollowers } from "../api/getPublisherFollowers";
import type { PublisherFollowersHistoryPoint } from "../types";
import { isAbortError } from "@/lib/utils/error";

type UsePublisherFollowersHistoryOptions = {
  clanid: number;
  range: string;
  resolution?: "raw" | "hourly" | "daily" | "weekly" | "monthly";
  bust?: number;
  enabled?: boolean;
};

export function usePublisherFollowersHistory({
  clanid,
  range,
  resolution = "monthly",
  bust,
  enabled = true,
}: UsePublisherFollowersHistoryOptions) {
  const [history, setHistory] = useState<PublisherFollowersHistoryPoint[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleStart = useEffectEvent(() => {
    setIsLoading(true);
    setError(null);
  });

  const handleSuccess = useEffectEvent((result: PublisherFollowersHistoryPoint[]) => {
    setHistory(result);
    setIsLoading(false);
  });

  const handleError = useEffectEvent((message: string) => {
    setHistory([]);
    setError(message);
    setIsLoading(false);
  });

  useEffect(() => {
    if (!enabled) {
      handleSuccess([]);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    handleStart();

    getPublisherFollowers(clanid, range, resolution, bust, {
      signal: controller.signal,
    })
      .then((result) => {
        if (!cancelled) handleSuccess(result);
      })
      .catch((err: Error) => {
        // Ignore aborts
        if (isAbortError(err)) return;
        if (!cancelled) handleError(err.message ?? "Unknown error");
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [clanid, range, resolution, bust, enabled]);

  return { history, isLoading, error };
}

